import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewReport = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [currentReportId, setCurrentReportId] = useState(null);
  
  // Settings from database
  const [settings, setSettings] = useState({
    veicoli: [],
    personale: [],
    turni_options: [],
    extratime_options: []
  });
  const [settingsLoading, setSettingsLoading] = useState(true);

  const [formData, setFormData] = useState({
    titolo_evento: '',
    data_evento: '',
    veicoli_usati: [],
    veicolo_altro: '',
    rimborsi_veicoli: '',
    danni_veicoli: '',
    turni_personale: [],
    altri_personale: '',
    extratime_ore: 0,
    materiale_extra: '',
    orario_partenza: '',
    orario_arrivo: '',
    orario_inizio_evento: '',
    orario_fine_evento: '',
    orario_fine_smontaggio: '',
    orario_rientro: '',
    riepilogo_spese: '',
    spese_altro: '',
    materiale_guasto: '',
    problemi_cliente: '',
    problemi_personale: ''
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedMaterialeGuastoFiles, setSelectedMaterialeGuastoFiles] = useState([]);
  const [turniMap, setTurniMap] = useState({});

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVeicoliChange = (veicolo) => {
    setFormData(prev => {
      const veicoli = prev.veicoli_usati.includes(veicolo)
        ? prev.veicoli_usati.filter(v => v !== veicolo)
        : [...prev.veicoli_usati, veicolo];
      return { ...prev, veicoli_usati: veicoli };
    });
  };

  const handleTurnoChange = (persona, ore) => {
    setTurniMap(prev => {
      const newMap = { ...prev };
      if (ore === 0) {
        delete newMap[persona];
      } else {
        newMap[persona] = ore;
      }
      return newMap;
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleCameraCapture = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMaterialeGuastoFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedMaterialeGuastoFiles(prev => [...prev, ...files]);
  };

  const handleMaterialeGuastoCameraCapture = (e) => {
    const files = Array.from(e.target.files);
    setSelectedMaterialeGuastoFiles(prev => [...prev, ...files]);
  };

  const removeMaterialeGuastoFile = (index) => {
    setSelectedMaterialeGuastoFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (reportId, files, fileType = 'spese') => {
    if (files.length === 0) return;

    setUploadingFiles(true);
    try {
      for (const file of files) {
        const reader = new FileReader();
        const fileData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result.split(',')[1]);
          reader.readAsDataURL(file);
        });

        await axios.post(
          `${API}/reports/${reportId}/upload`,
          {
            file_data: fileData,
            filename: `${fileType}_${file.name}`,
            content_type: file.type,
            file_type: fileType
          },
          {
            headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
          }
        );
      }
    } catch (error) {
      console.error('Errore upload file:', error);
      alert('Errore durante il caricamento dei file');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepara turni personale
      const turni_personale = Object.entries(turniMap).map(([nome, ore]) => ({
        nome,
        ore
      }));

      const reportData = {
        ...formData,
        turni_personale
      };

      const response = await axios.post(`${API}/reports`, reportData, {
        headers: getAuthHeader()
      });

      const reportId = response.data.id;
      setCurrentReportId(reportId);

      // Upload files spese se presenti
      if (selectedFiles.length > 0) {
        await uploadFiles(reportId, selectedFiles, 'spese');
      }

      // Upload files materiale guasto se presenti
      if (selectedMaterialeGuastoFiles.length > 0) {
        await uploadFiles(reportId, selectedMaterialeGuastoFiles, 'materiale_guasto');
      }

      alert('Report creato con successo!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Errore creazione report:', error);
      alert('Errore durante la creazione del report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="new-report-page">
      {/* Navbar */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">BETTI SRL</h1>
                <p className="text-xs text-gray-500">Nuovo Report Evento</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Torna alla Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {settingsLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informazioni Base */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Informazioni Evento</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titolo Evento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.titolo_evento}
                  onChange={(e) => handleInputChange('titolo_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="titolo-evento-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Evento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.data_evento}
                  onChange={(e) => handleInputChange('data_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="data-evento-input"
                />
              </div>
            </div>
          </div>

          {/* Veicoli */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Veicoli Usati</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Veicoli <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {(settings.veicoli || []).map((veicolo) => (
                    <label key={veicolo} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition">
                      <input
                        type="checkbox"
                        checked={formData.veicoli_usati.includes(veicolo)}
                        onChange={() => handleVeicoliChange(veicolo)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        data-testid={`veicolo-checkbox-${veicolo}`}
                      />
                      <span className="text-gray-700">{veicolo}</span>
                    </label>
                  ))}
                  <div>
                    <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.veicolo_altro !== ''}
                        onChange={(e) => !e.target.checked && handleInputChange('veicolo_altro', '')}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">Altro:</span>
                    </label>
                    {formData.veicolo_altro !== undefined && (
                      <input
                        type="text"
                        value={formData.veicolo_altro}
                        onChange={(e) => handleInputChange('veicolo_altro', e.target.value)}
                        placeholder="Specifica altro veicolo"
                        className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        data-testid="veicolo-altro-input"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rimborsi per Veicoli Personali <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.rimborsi_veicoli}
                  onChange={(e) => handleInputChange('rimborsi_veicoli', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="rimborsi-veicoli-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Danni/Problemi/Incidenti al Veicolo
                </label>
                <textarea
                  value={formData.danni_veicoli}
                  onChange={(e) => handleInputChange('danni_veicoli', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="danni-veicoli-input"
                />
              </div>
            </div>
          </div>

          {/* Turni Personale */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Personale su Evento</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Turni (seleziona turno per persona)
                </label>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                        {settings.turni_options && settings.turni_options.map((turno) => (
                          <th key={turno} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{turno}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {settings.personale && settings.personale.length > 0 ? settings.personale.map((persona) => (
                        <tr key={persona} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-700">{persona}</td>
                          {settings.turni_options && settings.turni_options.map((turno) => (
                            <td key={turno} className="px-2 py-3 text-center">
                              <input
                                type="radio"
                                name={`turno-${persona}`}
                                checked={turniMap[persona] === turno}
                                onChange={() => handleTurnoChange(persona, turno)}
                                className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                                data-testid={`turno-${persona}-${turno}`}
                              />
                            </td>
                          ))}
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="10" className="px-4 py-3 text-sm text-gray-500 text-center">
                            Nessun personale configurato. Vai in Impostazioni.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome e Cognome e Turno di Altri non in Lista
                </label>
                <textarea
                  value={formData.altri_personale}
                  onChange={(e) => handleInputChange('altri_personale', e.target.value)}
                  rows="3"
                  placeholder="Es: Mario Rossi - 4 turni"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="altri-personale-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Extratime (turni)
                </label>
                <div className="flex space-x-4">
                  {(settings.extratime_options || []).length > 0 ? (settings.extratime_options || []).map((turno) => (
                    <label key={turno} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="extratime"
                        value={turno}
                        checked={formData.extratime_ore === turno}
                        onChange={() => handleInputChange('extratime_ore', turno)}
                        className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        data-testid={`extratime-${turno}`}
                      />
                      <span className="text-gray-700">{turno}</span>
                    </label>
                  )) : (
                    <p className="text-sm text-gray-500">Nessuna opzione extratime configurata.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Materiale Extra Last Minute <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.materiale_extra}
                  onChange={(e) => handleInputChange('materiale_extra', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="materiale-extra-input"
                />
              </div>
            </div>
          </div>

          {/* Orari */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Orari</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Partenza Magazzino <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={formData.orario_partenza}
                  onChange={(e) => handleInputChange('orario_partenza', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="orario-partenza-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Arrivo in Location
                </label>
                <input
                  type="time"
                  value={formData.orario_arrivo}
                  onChange={(e) => handleInputChange('orario_arrivo', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="orario-arrivo-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Inizio Evento
                </label>
                <input
                  type="time"
                  value={formData.orario_inizio_evento}
                  onChange={(e) => handleInputChange('orario_inizio_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="orario-inizio-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Fine Evento
                </label>
                <input
                  type="time"
                  value={formData.orario_fine_evento}
                  onChange={(e) => handleInputChange('orario_fine_evento', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="orario-fine-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Fine Smontaggio
                </label>
                <input
                  type="time"
                  value={formData.orario_fine_smontaggio}
                  onChange={(e) => handleInputChange('orario_fine_smontaggio', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="orario-smontaggio-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orario Rientro <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={formData.orario_rientro}
                  onChange={(e) => handleInputChange('orario_rientro', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="orario-rientro-input"
                />
              </div>
            </div>
          </div>

          {/* Spese e Problemi */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Spese e Segnalazioni</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Riepilogo Spese <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.riepilogo_spese}
                  onChange={(e) => handleInputChange('riepilogo_spese', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="riepilogo-spese-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  File Spese (foto scontrini, ecc.)
                </label>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 cursor-pointer transition">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Scatta Foto
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleCameraCapture}
                        className="hidden"
                        multiple
                        data-testid="camera-input"
                      />
                    </label>
                    <label className="flex items-center px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 cursor-pointer transition">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Carica da Galleria
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        multiple
                        data-testid="gallery-input"
                      />
                    </label>
                  </div>
                  
                  {selectedFiles.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                            data-testid={`remove-file-${index}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altro da Segnalare (Spese)
                </label>
                <textarea
                  value={formData.spese_altro}
                  onChange={(e) => handleInputChange('spese_altro', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="spese-altro-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Materiale Guasto da Segnalare
                </label>
                <textarea
                  value={formData.materiale_guasto}
                  onChange={(e) => handleInputChange('materiale_guasto', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="materiale-guasto-input"
                />
                
                {/* Upload foto materiale guasto */}
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto Materiale Guasto
                  </label>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-3">
                      <label className="flex items-center px-4 py-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 cursor-pointer transition">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Scatta Foto
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleMaterialeGuastoCameraCapture}
                          className="hidden"
                          multiple
                          data-testid="materiale-guasto-camera-input"
                        />
                      </label>
                      <label className="flex items-center px-4 py-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 cursor-pointer transition">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Carica da Galleria
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleMaterialeGuastoFileSelect}
                          className="hidden"
                          multiple
                          data-testid="materiale-guasto-gallery-input"
                        />
                      </label>
                    </div>
                    
                    {selectedMaterialeGuastoFiles.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {selectedMaterialeGuastoFiles.map((file, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Materiale guasto ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border-2 border-orange-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeMaterialeGuastoFile(index)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                              data-testid={`remove-materiale-guasto-file-${index}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Problemi con Cliente/Location/ecc
                </label>
                <textarea
                  value={formData.problemi_cliente}
                  onChange={(e) => handleInputChange('problemi_cliente', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="problemi-cliente-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Problemi Comportamentali Personale
                </label>
                <textarea
                  value={formData.problemi_personale}
                  onChange={(e) => handleInputChange('problemi_personale', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="problemi-personale-input"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
              disabled={loading || uploadingFiles}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || uploadingFiles}
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-report-btn"
            >
              {loading ? 'Salvataggio in corso...' : uploadingFiles ? 'Caricamento file...' : 'Crea Report'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default NewReport;